import React, { useState } from 'react';

import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';

import {
  Alert,
  Box,
  Card,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { TourOfferConfiguration } from '~/types/tours/tourOffer';

import ConfigurationEditor from '../Bundles/ConfigurationEditor';

import NativeTourCopyFromOption from './NativeTourCopyFromOption';
import NativeTourDepartureItineraries from './NativeTourDepartureItineraries';
import NativeTourItineraryEditor from './NativeTourItineraryEditor';
import NativeTourOptionDetails, { NativeTourOptionDetailsArea } from './NativeTourOptionDetails';
import NativeTourOptionHotels from './NativeTourOptionHotels';
import {
  NativeTourForm,
  duplicateTitles,
  isItineraryInvalid,
  isOptionContentValid,
  optionDetailsProblems,
} from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
  setValue: UseFormSetValue<NativeTourForm>;
  tourOfferId: string;
  leastPax: number;
  configurations: Array<TourOfferConfiguration>;
  changedConfigurationIds: Array<string>;
  changedOptionDetailIds: Array<string>;
  changedOptionHotelIds: Array<string>;
  changedOptionItineraryIds: Array<string>;
  hasSeveralSuppliers: boolean;
  /** A Salesforce tour's itinerary is its own tab; a native-only offer's is edited with its content. */
  isSalesforceTour: boolean;
}

type OptionArea = 'about' | 'inclusions' | 'itinerary' | 'details' | 'hotels';

// One row of tabs, in the order an option is set up.
const AREAS: ReadonlyArray<{ key: OptionArea; label: string }> = [
  { key: 'about', label: 'About' },
  { key: 'inclusions', label: 'Inclusions' },
  { key: 'itinerary', label: 'Itinerary' },
  { key: 'details', label: 'Details & route' },
  { key: 'hotels', label: 'Hotels' },
];

/** Every tour option: its copy, what's included, its itinerary, who it suits and its route, and its hotels. */
export default function NativeTourOptionsSection({
  control,
  setValue,
  tourOfferId,
  leastPax,
  configurations,
  changedConfigurationIds,
  changedOptionDetailIds,
  changedOptionHotelIds,
  changedOptionItineraryIds,
  hasSeveralSuppliers,
  isSalesforceTour,
}: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [area, setArea] = useState<OptionArea>('about');
  const contents = useWatch({ control, name: 'configurations' });
  const details = useWatch({ control, name: 'optionDetails' });
  const itineraries = useWatch({ control, name: 'optionItineraries' });
  const hosts = useWatch({ control, name: 'content.hosts' });

  if (configurations.length === 0) {
    return (
      <Alert severity="info">
        This tour has no tour options yet. They come from its supplier contracts in Salesforce.
      </Alert>
    );
  }

  const active = configurations.find((configuration) => configuration.id === activeId) ?? configurations[0];
  const stateOf = (id: string) => {
    const isChanged =
      changedConfigurationIds.includes(id) ||
      changedOptionDetailIds.includes(id) ||
      changedOptionHotelIds.includes(id) ||
      changedOptionItineraryIds.includes(id);
    const isInvalid =
      (changedConfigurationIds.includes(id) && !isOptionContentValid(contents[id])) ||
      (changedOptionDetailIds.includes(id) && Object.keys(optionDetailsProblems(details[id], leastPax)).length > 0) ||
      // As the nav's check does, so the option blocking the save is the one marked.
      (changedOptionItineraryIds.includes(id) &&
        isItineraryInvalid(
          itineraries[id],
          configurations.find((configuration) => configuration.id === id)?.lengthInDays ?? null,
        ));
    return { isChanged, isInvalid };
  };
  const repeated = duplicateTitles(contents[active.id]?.inclusions.map((entry) => entry.title) ?? []);
  const optionDetails = (part: NativeTourOptionDetailsArea) => (
    <Controller
      key={`${active.id}-${part}`}
      control={control}
      name={`optionDetails.${active.id}`}
      render={({ field }) => (
        <NativeTourOptionDetails
          area={part}
          configuration={active}
          value={field.value}
          onChange={field.onChange}
          leastPax={leastPax}
        />
      )}
    />
  );

  return (
    <Stack spacing={2}>
      {hasSeveralSuppliers && (
        <Alert severity="info">
          This tour has more than one supplier. Edits apply to every supplier's copy of the option.
        </Alert>
      )}
      <Grid container spacing={2}>
        {configurations.length > 1 && (
          <Grid size={{ xs: 12, md: 3 }}>
            <Card variant="outlined" sx={{ position: 'sticky', top: 16 }}>
              <List dense disablePadding>
                {configurations.map((configuration) => {
                  const { isChanged, isInvalid } = stateOf(configuration.id);
                  return (
                    <ListItemButton
                      key={configuration.id}
                      selected={configuration.id === active.id}
                      onClick={() => setActiveId(configuration.id)}
                    >
                      <ListItemText
                        primary={contents[configuration.id]?.title || configuration.name}
                        secondary={isInvalid ? 'Needs fixing' : isChanged ? 'Edited' : configuration.name}
                        secondaryTypographyProps={{
                          color: isInvalid ? 'error' : isChanged ? 'primary' : 'text.secondary',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Card>
          </Grid>
        )}
        <Grid size={{ xs: 12, md: configurations.length > 1 ? 9 : 12 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
              <Box>
                <Typography variant="h6">{contents[active.id]?.title || active.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {active.name}
                </Typography>
              </Box>
              <NativeTourCopyFromOption
                control={control}
                setValue={setValue}
                target={active}
                configurations={configurations}
                isSalesforceTour={isSalesforceTour}
              />
            </Stack>
            <Tabs
              value={area}
              onChange={(_event, next: OptionArea) => setArea(next)}
              variant="scrollable"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {AREAS.map(({ key, label }) => (
                <Tab key={key} value={key} label={label} />
              ))}
            </Tabs>
            {(area === 'about' || area === 'inclusions' || (area === 'itinerary' && !isSalesforceTour)) && (
              <Stack spacing={4}>
                {area === 'inclusions' && repeated.length > 0 && (
                  <Alert severity="error">
                    Listed more than once: {repeated.join(', ')}. Customers would see them twice.
                  </Alert>
                )}
                <Controller
                  key={`${active.id}-${area}`}
                  control={control}
                  name={`configurations.${active.id}`}
                  render={({ field }) => (
                    <ConfigurationEditor
                      configuration={active}
                      value={field.value}
                      onChange={field.onChange}
                      sections={
                        area === 'about'
                          ? ['about', 'highlights']
                          : area === 'inclusions'
                            ? ['inclusions']
                            : ['itinerary']
                      }
                    />
                  )}
                />
                {area === 'inclusions' && optionDetails('packages')}
              </Stack>
            )}
            {area === 'itinerary' && isSalesforceTour && (
              <Controller
                key={`${active.id}-itinerary`}
                control={control}
                name={`optionItineraries.${active.id}`}
                render={({ field }) => (
                  <Stack spacing={3}>
                    <NativeTourItineraryEditor
                      value={field.value}
                      onChange={field.onChange}
                      lengthInDays={active.lengthInDays}
                      hosts={hosts.filter((host) => !!host.svcImageId)}
                    />
                    <NativeTourDepartureItineraries
                      tourOfferId={tourOfferId}
                      configurationId={active.id}
                      optionItinerary={field.value}
                      hosts={hosts.filter((host) => !!host.svcImageId)}
                    />
                  </Stack>
                )}
              />
            )}
            {area === 'details' && (
              <Stack spacing={3}>
                {optionDetails('details')}
                {optionDetails('route')}
              </Stack>
            )}
            {area === 'hotels' && (
              <Controller
                key={`${active.id}-hotels`}
                control={control}
                name={`optionHotels.${active.id}`}
                render={({ field }) => (
                  <NativeTourOptionHotels
                    configurationId={active.id}
                    value={field.value}
                    onChange={field.onChange}
                    startLocation={details[active.id]?.route.startLocationName ?? null}
                    endLocation={details[active.id]?.route.endLocationName ?? null}
                  />
                )}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
